/**
 * Où chaque navigateur pose sa question d'accès, et où l'on rend un accès refusé : la seule source de
 * ces chemins, lue par le test d'entrée (l'échelle « autorisation », `troubleshootingLadders`) et par le
 * live (le guidage de la question, la boîte du micro bloqué).
 *
 * Deux règles du test d'entrée tiennent ici :
 *  - **jamais un côté de l'écran dans une phrase** : les navigateurs déplacent leurs boutons (Chrome a
 *    rangé les réglages du site dans la puce de l'adresse, là où il dessinait une caméra barrée de
 *    l'autre côté). On nomme l'icône, et le guidage la dessine ;
 *  - **un geste par ligne**, le verbe en premier, deux lignes au plus : le rechargement est dit à part.
 *
 * `prompt` : là où la question s'ouvre, pour la flèche du guidage. Chrome, Edge et Firefox la
 * suspendent à la barre d'adresse, en haut à gauche ; Safari sur Mac la fait descendre de la barre, au
 * milieu ; sur un téléphone ou une tablette, elle s'ouvre au milieu de l'écran, sans flèche.
 */

import { browserFamily } from "./verifiedMemory"

/** Le navigateur, tel que ses chemins le distinguent : `browserFamily` sépare de plus le téléphone. */
export type PermissionBrowser = "chrome" | "edge" | "firefox" | "safari" | "ios" | "android" | "autre"

export type PermissionDevice = "microphone" | "camera"

/**
 * Une ligne du chemin : son glyphe (un nom FontAwesome 5, que le design system dessine) et le geste.
 * `choice` : la ligne où l'élève choisit « Autoriser », sous laquelle le test d'entrée dessine la
 * question qui va s'ouvrir (Firefox n'en a pas : on y retire « Bloqué »).
 */
export type PermissionStep = { icon: string; text: string; choice?: true }

export type PromptPlace = "top-left" | "top-center" | null

type DeviceWords = { name: string; label: string; glyph: string; blockedGlyph: string; blocked: string }

const DEVICE: Record<PermissionDevice, DeviceWords> = {
    microphone: {
        name: "le micro",
        label: "Micro",
        glyph: "microphone",
        blockedGlyph: "microphone-slash",
        blocked: "le micro barré",
    },
    camera: {
        name: "la caméra",
        label: "Caméra",
        glyph: "video",
        blockedGlyph: "video-slash",
        blocked: "la caméra barrée",
    },
}

type BrowserPath = { prompt: PromptPlace; steps: (device: DeviceWords) => PermissionStep[] }

const choose = (device: DeviceWords): PermissionStep => ({
    icon: device.glyph,
    text: `À côté de ${device.label}, choisis « Autoriser ».`,
    choice: true,
})

const PATHS: Record<PermissionBrowser, BrowserPath> = {
    chrome: {
        prompt: "top-left",
        steps: (device) => [
            { icon: "sliders-h", text: "Clique sur l'icône des réglages, juste à côté de l'adresse du site." },
            choose(device),
        ],
    },
    edge: {
        prompt: "top-left",
        steps: (device) => [
            { icon: "lock", text: "Clique sur le cadenas, juste à côté de l'adresse du site." },
            choose(device),
        ],
    },
    firefox: {
        prompt: "top-left",
        steps: (device) => [
            { icon: device.blockedGlyph, text: `Clique sur ${device.blocked}, juste à côté de l'adresse du site.` },
            { icon: "times", text: "Clique sur la croix à côté de « Bloqué »." },
        ],
    },
    safari: {
        prompt: "top-center",
        steps: (device) => [
            {
                icon: "cog",
                text: "Dans le menu tout en haut, clique sur Safari, puis sur « Réglages pour ce site web ».",
            },
            choose(device),
        ],
    },
    ios: {
        prompt: null,
        steps: (device) => [
            { icon: "font", text: "Touche « aA », à côté de l'adresse du site, puis « Réglages du site web »." },
            choose(device),
        ],
    },
    android: {
        prompt: null,
        steps: (device) => [
            { icon: "lock", text: "Touche l'icône juste à côté de l'adresse du site, puis « Autorisations »." },
            choose(device),
        ],
    },
    autre: {
        prompt: "top-left",
        steps: (device) => [
            {
                icon: device.blockedGlyph,
                text: "Cherche la petite icône caméra ou réglages, juste à côté de l'adresse du site. Clique dessus.",
            },
            { icon: device.glyph, text: `Choisis « Autoriser » pour ${device.name}.`, choice: true },
        ],
    },
}

/**
 * Quand c'est la confidentialité de l'ordinateur qui bloque, et pas le navigateur : ses Réglages, puis
 * le navigateur à autoriser. Le même chemin partout.
 */
const SYSTEM_STEPS: Record<PermissionDevice, PermissionStep[]> = {
    microphone: [
        { icon: "cog", text: "Ton ordinateur bloque le micro. Ouvre ses Réglages, puis Confidentialité, puis Micro." },
        { icon: "unlock", text: "Autorise ton navigateur (Chrome, Firefox…)." },
    ],
    camera: [
        {
            icon: "cog",
            text: "Ton ordinateur bloque la caméra. Ouvre ses Réglages, puis Confidentialité, puis Caméra.",
        },
        { icon: "unlock", text: "Autorise ton navigateur, puis recharge la page." },
    ],
}

/** Les navigateurs que les chemins distinguent. */
export const PERMISSION_BROWSERS = Object.keys(PATHS) as PermissionBrowser[]

/** Un navigateur que les chemins ne connaissent pas : celui d'« autre ». */
const pathOf = (browser: string): BrowserPath => (browser in PATHS ? PATHS[browser as PermissionBrowser] : PATHS.autre)

/** Le chemin d'un navigateur (`browserFamily`, ou `permissionBrowserOf` pour séparer le téléphone). */
export function permissionPathFor(browser: string, device: PermissionDevice): PermissionStep[] {
    return pathOf(browser).steps(DEVICE[device])
}

/** Le chemin quand l'ordinateur bloque l'appareil (sa confidentialité), quel que soit le navigateur. */
export function systemPermissionPath(device: PermissionDevice): PermissionStep[] {
    return SYSTEM_STEPS[device]
}

/** La ligne « Autoriser » du chemin (`choice`), ou `null` quand il n'en a pas. */
export function permissionChoiceIndex(browser: string): number | null {
    const index = permissionPathFor(browser, "microphone").findIndex((step) => step.choice === true)
    return index >= 0 ? index : null
}

/** « le micro », « la caméra ». */
export function permissionDeviceName(device: PermissionDevice): string {
    return DEVICE[device].name
}

/** Là où la question du navigateur s'ouvre, pour la flèche du guidage. */
export function promptPlaceOf(browser: string): PromptPlace {
    return pathOf(browser).prompt
}

/**
 * Le navigateur de la page, téléphone séparé : sur iPhone et iPad, tous les navigateurs sont Safari
 * dessous, et la question s'ouvre au milieu de l'écran ; un iPad se présente comme un Mac, mais tactile.
 */
export function permissionBrowserOf(userAgent: string, maxTouchPoints = 0): PermissionBrowser {
    const ua = userAgent.toLowerCase()
    if (/iphone|ipad|ipod/.test(ua) || (ua.includes("macintosh") && maxTouchPoints > 1)) return "ios"
    if (ua.includes("android")) return "android"
    return browserFamily(userAgent) as PermissionBrowser
}

/**
 * Le navigateur de la page en cours, au web ; `autre` ailleurs. L'app n'a pas de `navigator.userAgent`
 * (React Native) : la garde suffit, sans lire la plateforme — ce module reste pur, les vues-modèles le lisent.
 */
export function pagePermissionBrowser(): PermissionBrowser {
    if (typeof navigator === "undefined" || typeof navigator.userAgent !== "string") return "autre"
    return permissionBrowserOf(navigator.userAgent, navigator.maxTouchPoints ?? 0)
}
